import React, { useMemo, useState } from "react";

import {
  formatFractionDisplay,
  queryIsLoading,
  useGetFootprintingPorkFeedInformationByPrimaryDataId,
  useGetFootprintingPorkFeedProductions,
  useGetFootprintingPorkHerdDetails,
  useGetFootprintingPorkManureAdditive,
  useGetFootprintingPorkManureStorage,
  useGetManureAdditives,
  useGetPorkFeedTypes,
  useGetPorkManureStorage,
  useTranslation,
} from "utils";

import { FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE, PorkFootprintReturnBody } from "models";

import { Card, ListCard } from "components";

import { FootprintingPorkFeedProductionDrawer } from "pages/app/footprinting/components/FootprintingPorkFeedProductionDrawer";
import { ReviewPageTitle } from "pages/app/footprinting/components/ReviewPageTitle";
import { PorkFeedInformationForm } from "pages/app/footprinting/components/formFlows/porkFlow/PorkFeedInformationForm";
import { PorkHerdDetailsForm } from "pages/app/footprinting/components/formFlows/porkFlow/PorkHerdDetailsForm";
import { PorkManureAdditiveForm } from "pages/app/footprinting/components/formFlows/porkFlow/PorkManureAdditiveForm";
import { PorkManureStorageForm } from "pages/app/footprinting/components/formFlows/porkFlow/PorkManureStorageForm";
import { PorkMoorlandForm } from "pages/app/footprinting/components/formFlows/porkFlow/PorkMoorlandForm";
import { PorkPigletSourcingForm } from "pages/app/footprinting/components/formFlows/porkFlow/PorkPigletSourcingForm";
import { PorkStableTypeForm } from "pages/app/footprinting/components/formFlows/porkFlow/PorkStableTypeForm";
import { PorkYieldForm } from "pages/app/footprinting/components/formFlows/porkFlow/PorkYieldForm";
import { PorkCommentForm } from "pages/app/footprinting/components/formFlows/shared/CommentForm";

interface FootprintEditPorkDetailsCardProps {
  porkFootprintData: PorkFootprintReturnBody;
  isPorkFootprintDataLoading: boolean;
  setShowEditModal: (show: boolean) => void;
  setPorkModalForm: (formType: string) => void;
  farmId: string;
  year: number;
}

const FootprintEditPorkDetailsCard = ({
  porkFootprintData,
  isPorkFootprintDataLoading,
  setShowEditModal,
  setPorkModalForm,
  farmId,
  year,
}: FootprintEditPorkDetailsCardProps) => {
  const { t } = useTranslation(["footprintingPork", "footprinting", "pork"]);

  const feedTypesQuery = useGetPorkFeedTypes();
  const manureStorageQuery = useGetPorkManureStorage();
  const manureAdditivesQuery = useGetManureAdditives();

  const { footprint } = porkFootprintData;

  const herdDetailsQuery = useGetFootprintingPorkHerdDetails(footprint?.id ?? "", { enabled: !!footprint?.id });
  const feedInformationQuery = useGetFootprintingPorkFeedInformationByPrimaryDataId(footprint?.id ?? "");
  const manureStorageUsageQuery = useGetFootprintingPorkManureStorage(footprint?.id);
  const manureAdditiveUsageQuery = useGetFootprintingPorkManureAdditive(footprint?.id);

  const selfProducedFeeds = useMemo(() => (feedInformationQuery.data ?? []).filter((feedInfo) => feedInfo.isSelfProduced), [feedInformationQuery.data]);
  const feedProductionQueries = useGetFootprintingPorkFeedProductions(selfProducedFeeds.map((feedInfo) => feedInfo.id ?? ""));

  const [selectedFeedInformationId, setSelectedFeedInformationId] = useState<string | null>(null);

  // Verified footprints are read-only: the admin unverify is the only write the API accepts
  const editButton = (formKey: string) =>
    footprint?.isVerified
      ? undefined
      : {
          label: t("footprinting:review.edit"),
          onClick: () => {
            setShowEditModal(true);
            setPorkModalForm(formKey);
          },
        };

  const displayRadioAnswer = (isActive: boolean | null) => (isActive === null ? "" : t(`footprinting:radioAnswer.${isActive}`, { ns: "footprinting" }));
  const isLoading =
    isPorkFootprintDataLoading ||
    [feedTypesQuery, manureStorageQuery, manureAdditivesQuery, herdDetailsQuery, feedInformationQuery, manureStorageUsageQuery, manureAdditiveUsageQuery].some(
      queryIsLoading
    ) ||
    feedProductionQueries.some(queryIsLoading);

  const pigletSourcing = footprint?.pigletSourcing;

  return (
    <Card className="admin-edit-pork-details-card padding-125">
      <ReviewPageTitle title={t("review.pork.title", { ns: "footprinting" })} iconName="tabler/pig" isLoading={isLoading} />
      {/* Yield */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.porkYield.cardTitle")}
        titleButton={editButton(PorkYieldForm.Key)}
        dataPairs={[
          { label: t("porkYieldForm.question.meatYieldAmountLabel"), value: `${footprint?.yieldKgs ?? ""}` },
          { label: t("porkYieldForm.question.liveWeightLabel"), value: `${footprint?.liveWeight ?? ""}` },
        ]}
      />
      {/* Herd details */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.herdDetails.cardTitle")}
        titleButton={editButton(PorkHerdDetailsForm.Key)}
        dataPairs={[
          {
            label: t("porkHerdDetailsForm.question.averageHerdSizeInput"),
            value: `${herdDetailsQuery.data?.averageHerdSize ?? ""} ${t("porkHerdDetailsForm.question.averageHerdSizeSuffix")}`,
          },
          {
            label: t("porkHerdDetailsForm.question.rearingPeriodDaysInput"),
            value: `${herdDetailsQuery.data?.rearingPeriodDays ?? ""} ${t("porkHerdDetailsForm.question.rearingPeriodDaysSuffix")}`,
          },
          {
            label: t("porkHerdDetailsForm.question.pigLossesInput"),
            value: `${formatFractionDisplay(herdDetailsQuery.data?.pigLossPct ?? 0)} %`,
          },
        ]}
      />
      {/* Stable type — one card per stable, all edited via the same shared multi-entry form */}
      {(footprint?.stables ?? []).map((stable, index) => (
        <ListCard
          key={stable.id}
          className="margin-bottom-100"
          isOutlined
          isLoading={isLoading}
          title={t("porkStableTypeForm.stableNumber", { count: index + 1 })}
          titleButton={editButton(PorkStableTypeForm.Key)}
          dataPairs={[
            { label: t("porkStableTypeForm.question.stableTypeLabel"), value: t(`porkStableTypeForm.stableTypeOptions.${stable.stableType}`) },
            {
              label: t("porkStableTypeForm.question.stableSizeLabel"),
              value: `${stable.stableSize ?? ""} ${t("porkStableTypeForm.question.stableSizeSuffix")}`,
            },
            { label: t("porkStableTypeForm.question.ammoniaFiltrationQuestion"), value: displayRadioAnswer(stable.hasAmmoniaFiltration) },
            ...(stable.hasAmmoniaFiltration
              ? [
                  {
                    label: t("porkStableTypeForm.question.ammoniaEfficiencyLabel"),
                    value: t(`porkStableTypeForm.ammoniaEfficiencyOptions.${stable.ammoniaFiltrationEfficiency}`),
                  },
                ]
              : []),
          ]}
        />
      ))}
      {/* Piglet sourcing */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("categories.pigletSourcing")}
        titleButton={editButton(PorkPigletSourcingForm.Key)}
        dataPairs={[
          {
            label: t("porkPigletSourcingForm.sourcingTypeQuestion"),
            value: pigletSourcing?.sourcingType ? t(`porkPigletSourcingForm.sourcingTypeOptions.${pigletSourcing.sourcingType}`) : "",
          },
        ]}
      />
      {(pigletSourcing?.sourceFarms ?? []).map((sourceFarm, index) => (
        <ListCard
          key={sourceFarm.id ?? index}
          className="margin-bottom-100"
          isOutlined
          isLoading={isLoading}
          title={t("porkPigletSourcingForm.sourceFarmNumber", { count: index + 1 })}
          titleButton={editButton(PorkPigletSourcingForm.Key)}
          dataPairs={[
            { label: t("porkPigletSourcingForm.question.sourceFarmLabel"), value: sourceFarm.sourceFarmName ?? "" },
            ...(sourceFarm.vvvoIdentifier ? [{ label: t("porkPigletSourcingForm.question.vvvoIdentifierLabel"), value: sourceFarm.vvvoIdentifier }] : []),
            {
              label: t("porkPigletSourcingForm.question.quantityPurchasedLabel"),
              value: `${sourceFarm.quantityPurchased ?? ""} ${t("porkPigletSourcingForm.question.quantityPurchasedSuffix")}`,
            },
            {
              label: t("porkPigletSourcingForm.question.averageWeightLabel"),
              value: `${sourceFarm.averageWeightKg ?? ""} ${t("porkPigletSourcingForm.question.averageWeightSuffix")}`,
            },
          ]}
        />
      ))}
      {/* Feed types / amounts */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.feedInformation.cardTitle")}
        titleButton={editButton(PorkFeedInformationForm.Key)}
        dataPairs={(feedInformationQuery.data ?? []).map((feedInfo) => {
          const feedType = feedTypesQuery.data?.find(({ id }) => id === feedInfo.feedId);
          return {
            label: feedType?.name ?? "",
            value: `${feedInfo.energyValue ?? ""} ${t("reviewAndSubmit.feedInformation.energyValueUnit")}`,
          };
        })}
      />
      {/* Self-produced feed production */}
      {selfProducedFeeds.map((feedInfo, index) => {
        const feedTypeName = feedTypesQuery.data?.find(({ id }) => id === feedInfo.feedId)?.name ?? "";
        const production = feedProductionQueries[index]?.data;
        const isSplitRelevant = production?.cropUsedForFeed === FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE.NO;

        return (
          <ListCard
            key={feedInfo.id}
            className="margin-bottom-100"
            isOutlined
            isLoading={isLoading}
            title={t("reviewAndSubmit.feedInformation.selfProducedTitle", { feedTypeName: feedTypeName || t("porkFeedProductionForm.title") })}
            titleButton={
              footprint?.isVerified ? undefined : { label: t("footprinting:review.edit"), onClick: () => setSelectedFeedInformationId(feedInfo.id ?? null) }
            }
            dataPairs={[
              {
                label: t("porkFeedProductionForm.question.cropUsageRadioTitle"),
                value: production?.cropUsedForFeed ? t(`porkFeedProductionForm.cropUsageOptions.${production.cropUsedForFeed}`) : "",
              },
              { label: t("porkFeedProductionForm.question.cropYieldTPerHa"), value: `${production?.cropYieldTPerHa ?? ""}` },
              { label: t("porkFeedProductionForm.question.dieselConsumptionLHa"), value: `${production?.dieselConsumptionLHa ?? ""}` },
              { label: t("porkFeedProductionForm.question.energyUsedKwhHa"), value: `${production?.energyUsedKwhHa ?? ""}` },
              { label: t("porkFeedProductionForm.question.mineralNitrogenKgHa"), value: `${production?.mineralNitrogenKgHa ?? ""}` },
              { label: t("porkFeedProductionForm.question.mineralPhosphorusKgHa"), value: `${production?.mineralPhosphorusKgHa ?? ""}` },
              { label: t("porkFeedProductionForm.question.mineralPotassiumKgHa"), value: `${production?.mineralPotassiumKgHa ?? ""}` },
              { label: t("porkFeedProductionForm.question.nitrogenOrganicFertKgHa"), value: `${production?.nitrogenOrganicFertKgHa ?? ""}` },
              { label: t("porkFeedProductionForm.question.totalLimeAppliedKgCaCo3PerHa"), value: `${production?.totalLimeAppliedKgCaCo3PerHa ?? ""}` },
              {
                label: t("porkFeedProductionForm.question.totalPlantProtectionApplied"),
                value: `${production?.totalPlantProtectionApplied ?? ""} ${production?.totalPlantProtectionAppliedUnit ?? ""}`.trim(),
              },
              {
                label: t("porkFeedProductionForm.question.totalWaterUsedIrregationLPerHa"),
                value: `${production?.totalWaterUsedIrregationLPerHa ?? ""}`,
              },
              ...(isSplitRelevant
                ? [
                    { label: t("porkFeedProductionForm.question.soldOffFarmPct"), value: `${formatFractionDisplay(production?.soldOffFarmPct ?? 0)} %` },
                    { label: t("porkFeedProductionForm.question.fedToPigsPct"), value: `${formatFractionDisplay(production?.fedToPigsPct ?? 0)} %` },
                    { label: t("porkFeedProductionForm.question.otherUsesPct"), value: `${formatFractionDisplay(production?.otherUsesPct ?? 0)} %` },
                  ]
                : []),
            ]}
          />
        );
      })}
      {/* Manure storage */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.manureStorage.cardTitle")}
        titleButton={editButton(PorkManureStorageForm.Key)}
        dataPairs={(manureStorageUsageQuery.data ?? []).map((manureStorageUsage) => ({
          label: manureStorageQuery.data?.find(({ id }) => id === manureStorageUsage.manureStorageId)?.name ?? "Unknown",
          value: `${formatFractionDisplay(manureStorageUsage.amountStoredPct)} %`,
        }))}
      />
      {/* Manure additives */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.manureAdditives.cardTitle")}
        titleButton={editButton(PorkManureAdditiveForm.Key)}
        dataPairs={[
          {
            label: t("reviewAndSubmit.manureAdditives.sluryAcidifiersUsed"),
            value: displayRadioAnswer(footprint?.manureAdditiveActive === "yes"),
          },
          ...(manureAdditiveUsageQuery.data ?? []).map((manureAdditiveUsage) => ({
            label: manureAdditivesQuery.data?.find(({ id }) => manureAdditiveUsage.manureAdditiveId === id)?.name ?? "",
            value: `${manureAdditiveUsage.amountUsedM3}`,
          })),
        ]}
      />
      {/* Moorland */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.moorland.cardTitle")}
        titleButton={editButton(PorkMoorlandForm.Key)}
        dataPairs={[
          {
            label: t("reviewAndSubmit.moorland.moorlandUsed"),
            value: displayRadioAnswer(footprint?.moorlandActive === "yes"),
          },
          ...(footprint?.moorlandActive === "yes"
            ? [
                {
                  label: t("reviewAndSubmit.moorland.fodderArea"),
                  value: `${footprint.moorlandAreaForFodderProduction} ${t("porkMoorlandForm.question.fodderProductionAreaSuffix")}`,
                },
                {
                  label: t("reviewAndSubmit.moorland.averageWaterLevel"),
                  value: `${footprint.moorlandAnnualWaterLevel} ${t("porkMoorlandForm.question.annualWaterLevelSuffix")}`,
                },
              ]
            : []),
        ]}
      />
      {/* Comment */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("commentForm.title", { ns: "footprinting" })}
        titleButton={editButton(PorkCommentForm.Key)}
        dataPairs={[
          {
            label: t("commentForm.label", { ns: "footprinting" }),
            value: footprint?.comment || t("commentForm.noComment", { ns: "footprinting" }),
          },
        ]}
      />
      {selectedFeedInformationId && (
        <FootprintingPorkFeedProductionDrawer
          farmId={farmId}
          year={year}
          visible={selectedFeedInformationId !== null}
          onClose={() => setSelectedFeedInformationId(null)}
          feedInformationId={selectedFeedInformationId}
          primaryDataId={footprint?.id}
          feedTypeName={
            feedTypesQuery.data?.find(({ id }) => id === selfProducedFeeds.find((feedInfo) => feedInfo.id === selectedFeedInformationId)?.feedId)?.name
          }
        />
      )}
    </Card>
  );
};

export default FootprintEditPorkDetailsCard;
